import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CoupleFeedbackClickInput, PutVenueReviewLinksInput, SubmitCoupleFeedbackInput, UpdateCoupleFeedbackInput } from '@lib/input/couple-feedback.input'
import { coupleFeedbackKeys } from './queryKeys'
import { coupleFeedbackService } from '@/services/CoupleFeedbackService'

export function useCoupleFeedbackPage(eventId: string) {
  return useQuery({
    queryKey: coupleFeedbackKeys.page(eventId),
    queryFn: () => coupleFeedbackService.page(eventId),
  })
}

export function useSubmitCoupleFeedback(eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SubmitCoupleFeedbackInput) => coupleFeedbackService.submit(eventId, input),
    onSuccess: (page) => queryClient.setQueryData(coupleFeedbackKeys.page(eventId), page),
  })
}

export function useCoupleFeedbackClick(eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CoupleFeedbackClickInput) => coupleFeedbackService.click(eventId, input),
    onSuccess: (page) => queryClient.setQueryData(coupleFeedbackKeys.page(eventId), page),
  })
}

export function useEventCoupleFeedback(eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: coupleFeedbackKeys.forEvent(eventId),
    queryFn: () => coupleFeedbackService.forEvent(eventId),
    enabled: enabled && eventId.length > 0,
  })
}

export function useCoupleFeedbackQueue() {
  return useQuery({ queryKey: coupleFeedbackKeys.queue(), queryFn: () => coupleFeedbackService.list() })
}

export function useCoupleFeedback(id: string) {
  return useQuery({ queryKey: coupleFeedbackKeys.detail(id), queryFn: () => coupleFeedbackService.get(id) })
}

export function useUpdateCoupleFeedback(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateCoupleFeedbackInput) => coupleFeedbackService.update(id, input),
    onSuccess: (row) => {
      queryClient.setQueryData(coupleFeedbackKeys.detail(id), row)
      void queryClient.invalidateQueries({ queryKey: coupleFeedbackKeys.queue() })
    },
  })
}

export function useVenueReviewLinks(venueId: string) {
  return useQuery({ queryKey: coupleFeedbackKeys.links(venueId), queryFn: () => coupleFeedbackService.links(venueId) })
}

/**
 * The Google listing and Facebook page the venue's GoHighLevel sub-accounts
 * have. Read from GoHighLevel each time, so kept a few minutes rather than
 * read again on every visit, and never retried: the list works without them.
 */
export function useReviewLinkSuggestions(venueId: string, enabled: boolean) {
  return useQuery({
    queryKey: coupleFeedbackKeys.suggestions(venueId),
    queryFn: () => coupleFeedbackService.suggestions(venueId),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useReplaceVenueReviewLinks(venueId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: PutVenueReviewLinksInput) => coupleFeedbackService.replaceLinks(venueId, input),
    onSuccess: (links) => queryClient.setQueryData(coupleFeedbackKeys.links(venueId), links),
  })
}
